import { ReactElement, useMemo, useState } from 'react';
import { Subscription } from '@/api';
import { useCompanyContext } from '../providers';
import {
    useOpenCancelSubscriptionPortalAction,
    useOpenPaymentMethodPortalAction,
    useOpenSubscriptionPortalAction,
    useOpenUpdateSubscriptionPortalAction
} from '../actions';
import { useFetchSubscriptionUsage } from '../queries';
import {
    UIIcon,
    UIMenuItemProps,
    UIModal,
    UIModalContent,
    UIModalFooter,
    UIModalHeader,
    UISection,
    UISectionContent,
    UISectionHeader,
    UIText,
    UITextButton
} from '@/ui';
import { blue, charcoal, moonlight, red } from '@/ui/styles';
import { useFormatDate } from '@/common/hooks';
import { Trans } from 'react-i18next';
import { useCompaniesTranslation } from '../hooks';

type SubscriptionSettingsSectionProps = {
    subscription: Subscription;
    canEdit: boolean;
};

export function SubscriptionSettingsSection(
    props: SubscriptionSettingsSectionProps
): ReactElement {
    const { subscription, canEdit } = props;

    const { company } = useCompanyContext();

    // Authoritative licensed-user count from the server, rather than counting
    // users on the client.
    const { usage } = useFetchSubscriptionUsage(company.id);
    const licensesUsed = usage?.app?.used ?? 0;

    const manageSubscription = useOpenSubscriptionPortalAction({
        companyId: company.id
    });
    const managePaymentMethod = useOpenPaymentMethodPortalAction({
        companyId: company.id
    });
    const viewInvoices = useOpenSubscriptionPortalAction({
        companyId: company.id
    });
    const updateSubscription = useOpenUpdateSubscriptionPortalAction({
        companyId: company.id
    });
    const cancelSubscription = useOpenCancelSubscriptionPortalAction({
        companyId: company.id
    });

    function onCancelSubscription() {
        // Errors handled in the action
        cancelSubscription.openCancelSubscriptionPortal().catch(() => {});
    }

    function openUpdateSubscriptionStripePortal() {
        // Errors handled in the action
        updateSubscription.openUpdateSubscriptionPortal().catch(() => {});
    }

    function onManagePaymentMethod() {
        // Errors handled in the action
        managePaymentMethod.openPaymentMethodPortal().catch(() => {});
    }

    function onViewInvoices() {
        // Errors handled in the action
        viewInvoices.openSubscriptionPortal().catch(() => {});
    }

    function onManagePress() {
        // Errors handled in the action
        manageSubscription.openSubscriptionPortal().catch(() => {});
    }

    return (
        <SubscriptionSettingsSectionForm
            subscription={subscription}
            licensesUsed={licensesUsed}
            onManagePress={onManagePress}
            openUpdateSubscriptionStripePortal={
                openUpdateSubscriptionStripePortal
            }
            onCancelPress={onCancelSubscription}
            onInvoiceHistoryPress={onViewInvoices}
            onManagePaymentMethod={onManagePaymentMethod}
            manageLoading={manageSubscription.loading}
            updateLoading={updateSubscription.loading}
            cancelLoading={cancelSubscription.loading}
            invoiceHistoryLoading={viewInvoices.loading}
            paymentMethodLoading={managePaymentMethod.loading}
            canEdit={canEdit}
            canUpdate={!company.isStripeAppAndLWDSubscriptionBundled}
            canCancel={!company.isStripeAppAndLWDSubscriptionBundled}
        />
    );
}

type SubscriptionSettingsSectionFormProps = {
    subscription: Subscription;
    licensesUsed: number;
    onManagePress: () => void;
    onManagePaymentMethod: () => void;
    onInvoiceHistoryPress: () => void;
    openUpdateSubscriptionStripePortal: () => void;
    onCancelPress: () => void;
    manageLoading: boolean;
    paymentMethodLoading: boolean;
    invoiceHistoryLoading: boolean;
    cancelLoading: boolean;
    updateLoading: boolean;
    canEdit: boolean;
    canUpdate: boolean;
    canCancel: boolean;
};

function SubscriptionSettingsSectionForm(
    props: SubscriptionSettingsSectionFormProps
): ReactElement {
    const {
        subscription,
        licensesUsed,
        onManagePress,
        onManagePaymentMethod,
        onInvoiceHistoryPress,
        openUpdateSubscriptionStripePortal,
        onCancelPress,
        manageLoading,
        paymentMethodLoading,
        invoiceHistoryLoading,
        cancelLoading,
        updateLoading,
        canEdit,
        canUpdate,
        canCancel
    } = props;

    const { t } = useCompaniesTranslation();
    const { formatDateOnly } = useFormatDate();

    const title = useMemo(() => {
        let type = '';
        switch (subscription.type) {
            case 'STANDARD':
                type = t('shared.subscription.plan_standard');
                break;
            case 'GUARDIAN_PLUS':
                type = t('shared.subscription.plan_guardian_plus');
                break;
            default:
                type = t('shared.subscription.plan_unknown');
        }

        let interval = '';
        switch (subscription.interval) {
            case 'day':
                interval = t('shared.subscription.interval_daily');
                break;
            case 'week':
                interval = t('shared.subscription.interval_weekly');
                break;
            case 'month':
                interval = t('shared.subscription.interval_monthly');
                break;
            case 'year':
                interval = t('shared.subscription.interval_annual');
                break;
            default:
                interval = t('shared.subscription.interval_unknown');
        }

        return `${type} - ${interval}`;
    }, [subscription.type, subscription.interval, t]);

    const subtitle = useMemo(() => {
        const periodEndStr = formatDateOnly(new Date(subscription.periodEnd));
        const periodStartStr = formatDateOnly(
            new Date(subscription.periodStart)
        );

        switch (subscription.status) {
            case 'active':
                if (subscription.cancelAtPeriodEnd) {
                    return t('shared.subscription.plan_expires_on', {
                        date: periodEndStr
                    });
                } else {
                    return t('shared.subscription.plan_renews_on', {
                        date: periodEndStr
                    });
                }
            case 'trialing':
                if (subscription.cancelAtPeriodEnd) {
                    return t('shared.subscription.trial_expires_on', {
                        date: periodEndStr
                    });
                } else {
                    return t('shared.subscription.trial_renews_on', {
                        date: periodEndStr
                    });
                }
            case 'incomplete':
                return t('shared.subscription.status_incomplete');
            case 'past_due':
                return t('shared.subscription.past_due_since', {
                    date: periodStartStr
                });
            case 'paused':
                return t('shared.subscription.paused');
            case 'canceled':
            case 'incomplete_expired':
            case 'unpaid':
                return t('shared.subscription.status_terminated');
        }
    }, [
        formatDateOnly,
        subscription.cancelAtPeriodEnd,
        subscription.periodEnd,
        subscription.periodStart,
        subscription.status,
        t
    ]);

    const featureItems = useMemo(() => {
        switch (subscription.type) {
            case 'STANDARD':
                return [
                    t('shared.subscription.feature_unlimited_tasks'),
                    t('shared.subscription.feature_unlimited_teams'),
                    t('shared.subscription.feature_mobile_apps'),
                    t('shared.subscription.feature_account_manager'),
                    t('shared.subscription.feature_sms_50')
                ];
            case 'GUARDIAN_PLUS':
                return [
                    t('shared.subscription.feature_unlimited_tasks'),
                    t('shared.subscription.feature_unlimited_teams'),
                    t('shared.subscription.feature_mobile_apps'),
                    t('shared.subscription.feature_account_manager'),
                    t('shared.subscription.feature_monitoring'),
                    t('shared.subscription.feature_sms_100')
                ];
            default:
                return [];
        }
    }, [subscription.type, t]);

    const licensesPercentage = useMemo(() => {
        const percent = (licensesUsed / subscription.quantity) * 100;
        return Math.min(100, Math.max(0, percent));
    }, [licensesUsed, subscription.quantity]);

    const rightMenuItems: UIMenuItemProps[] = useMemo(() => {
        if (!canEdit) {
            return [];
        }
        const items: UIMenuItemProps[] = [
            {
                text: t(
                    'components.subscription_settings_section.manage_subscription'
                ),
                leftIconName: 'sliders',
                loading: manageLoading,
                onClick: onManagePress
            },
            {
                text: t('shared.subscription.add_payment_method'),
                leftIconName: 'credit-card',
                onClick: onManagePaymentMethod,
                loading: paymentMethodLoading
            },
            {
                text: t('shared.subscription.invoice_history'),
                leftIconName: 'list',
                loading: invoiceHistoryLoading,
                onClick: onInvoiceHistoryPress
            }
        ];

        if (!subscription.cancelAtPeriodEnd && canCancel) {
            items.push({
                text: t(
                    'components.subscription_settings_section.cancel_subscription'
                ),
                textColor: red,
                leftIconName: 'x',
                loading: cancelLoading,
                onClick: onCancelPress
            });
        }

        return items;
    }, [
        manageLoading,
        onManagePress,
        onManagePaymentMethod,
        paymentMethodLoading,
        invoiceHistoryLoading,
        onInvoiceHistoryPress,
        subscription.cancelAtPeriodEnd,
        cancelLoading,
        onCancelPress,
        canEdit,
        canCancel,
        t
    ]);

    const periodEndStr = formatDateOnly(new Date(subscription.periodEnd));

    const [showContactUsToUpdateModal, setShowContactUsToUpdateModal] =
        useState(false);
    function onUpdatePress() {
        if (subscription.collectionMethod === 'charge_automatically') {
            openUpdateSubscriptionStripePortal();
        } else {
            setShowContactUsToUpdateModal(true);
        }
    }

    return (
        <>
            <UISection>
                <UISectionHeader
                    title={title}
                    subtitle={subtitle}
                    rightMenuItemsText={t('shared.labels.more')}
                    rightMenuItems={rightMenuItems}
                />
                {subscription.status === 'unpaid' && (
                    <UISectionContent borderBottom>
                        <div style={{ display: 'flex' }}>
                            <UIText>
                                <Trans
                                    ns="companies"
                                    i18nKey="shared.subscription.unpaid_notice"
                                    values={{
                                        status: t(
                                            'shared.subscription.status_unpaid'
                                        )
                                    }}
                                    components={{
                                        b: (
                                            <b>
                                                <span style={{ color: red }} />
                                            </b>
                                        )
                                    }}
                                />
                            </UIText>
                        </div>
                    </UISectionContent>
                )}
                {subscription.status === 'trialing' && (
                    <UISectionContent borderBottom>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            <UIText>
                                {/* Two whole sentences rather than one with an
                                    optional tail: only the caller knows whether
                                    the upgrade link is there, and a translator
                                    cannot place a link that may not exist. */}
                                <Trans
                                    ns="companies"
                                    i18nKey={
                                        canEdit
                                            ? 'components.subscription_settings_section.trialing_notice_upgrade'
                                            : 'components.subscription_settings_section.trialing_notice'
                                    }
                                    values={{
                                        status: t(
                                            'components.lwd_subscription_settings_section.status_trialling'
                                        )
                                    }}
                                    components={{
                                        b: (
                                            <b>
                                                <span style={{ color: blue }} />
                                            </b>
                                        ),
                                        a: (
                                            <span
                                                style={{
                                                    color: blue,
                                                    textDecoration: 'underline',
                                                    cursor: 'pointer'
                                                }}
                                                onClick={onUpdatePress}
                                            />
                                        )
                                    }}
                                />
                            </UIText>
                        </div>
                    </UISectionContent>
                )}
                {subscription.status !== 'trialing' &&
                    subscription.cancelAtPeriodEnd && (
                        <UISectionContent borderBottom>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                            >
                                <UIText>
                                    <Trans
                                        ns="companies"
                                        i18nKey="shared.subscription.expiring_notice"
                                        values={{ date: periodEndStr }}
                                        components={{
                                            a: (
                                                <a
                                                    href="https://www.safepointapp.com/contact"
                                                    style={{
                                                        color: blue,
                                                        textDecoration:
                                                            'underline'
                                                    }}
                                                />
                                            )
                                        }}
                                    />
                                </UIText>
                            </div>
                        </UISectionContent>
                    )}
                <UISectionContent
                    borderBottom={subscription.status !== 'trialing'}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 16
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 8
                            }}
                        >
                            {featureItems.map((item, index) => (
                                <div
                                    key={index}
                                    style={{ display: 'flex', gap: 16 }}
                                >
                                    <UIIcon
                                        name="check"
                                        size={20}
                                        color={charcoal}
                                    />
                                    <UIText>{item}</UIText>
                                </div>
                            ))}
                        </div>
                        {canEdit && (
                            <div>
                                <UITextButton
                                    variant="primary"
                                    onClick={onManagePress}
                                    loading={manageLoading}
                                    underline
                                >
                                    {t(
                                        'components.subscription_settings_section.manage_subscription'
                                    )}
                                </UITextButton>
                            </div>
                        )}
                    </div>
                </UISectionContent>

                {subscription.status !== 'trialing' && (
                    <UISectionContent>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 16
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    flexDirection: 'column',
                                    gap: 4
                                }}
                            >
                                <UIText>
                                    {t(
                                        'components.subscription_settings_section.licenses'
                                    )}
                                </UIText>
                                <UIText type="default2">
                                    {t('shared.subscription.licenses_used', {
                                        used: licensesUsed,
                                        total: subscription.quantity
                                    })}
                                </UIText>
                            </div>
                            <div
                                style={{
                                    height: 6,
                                    maxHeight: 6,
                                    width: '100%',
                                    backgroundColor: moonlight,
                                    borderRadius: 3
                                }}
                            >
                                <div
                                    style={{
                                        height: 6,
                                        maxHeight: 6,
                                        width: `${licensesPercentage}%`,
                                        backgroundColor: blue,
                                        borderRadius: 3
                                    }}
                                />
                            </div>
                            {canEdit && canUpdate && (
                                <div>
                                    <UITextButton
                                        variant="primary"
                                        onClick={onUpdatePress}
                                        loading={updateLoading}
                                        underline
                                    >
                                        {t(
                                            'components.subscription_settings_section.buy_more'
                                        )}
                                    </UITextButton>
                                </div>
                            )}
                        </div>
                    </UISectionContent>
                )}
            </UISection>
            <UIModal
                isOpen={showContactUsToUpdateModal}
                onClose={() => setShowContactUsToUpdateModal(false)}
            >
                {showContactUsToUpdateModal && (
                    <>
                        <UIModalHeader
                            title={t(
                                'components.subscription_settings_section.contact_title'
                            )}
                        />
                        <UIModalContent>
                            <UIText type="default2">
                                {t(
                                    'components.subscription_settings_section.contact_explainer'
                                )}
                            </UIText>
                        </UIModalContent>
                        <UIModalFooter
                            actions={[
                                {
                                    variant: 'primary',
                                    text: t(
                                        'components.subscription_settings_section.contact_us'
                                    ),
                                    onClick: () => {
                                        const url =
                                            'https://www.safepointapp.com/contact';
                                        window.open(url, '_blank');
                                        setShowContactUsToUpdateModal(false);
                                    }
                                },
                                {
                                    text: t('shared.labels.cancel'),
                                    onClick: () =>
                                        setShowContactUsToUpdateModal(false)
                                }
                            ]}
                        />
                    </>
                )}
            </UIModal>
        </>
    );
}
